import { useRef } from "react";
import { Loader2 } from "lucide-react";
import { usePointEditStore } from "../../stores/pointEditStore";
import { useUiStore } from "../../stores/uiStore";
import { useZoneCreateStore } from "../../stores/zoneCreateStore";

export const SOIL_SAMPLE_BTN =
  "rounded border border-line px-2 py-1 text-xs text-fg hover:bg-hover disabled:opacity-50";

const ORDER_ACCEPT = {
  zones: ".zip,.shp,.kml,.kmz,.geojson,.json",
  points: ".csv,.geojson,.json",
} as const;

const FORMAT_HINT = {
  zones: ".zip / .shp / .kml / .geojson",
  points: ".csv (lat/long + values) / .geojson",
} as const;

function takeFile(
  input: HTMLInputElement | null,
  onFile: (file: File) => void,
  file: File | undefined,
) {
  if (file) onFile(file);
  if (input) input.value = "";
}

// The two map sessions are mutually exclusive. A stale one left over from an X-closed
// map would otherwise win the mount and show the wrong editor.
function startCreate(kind: "zones" | "points", fieldId: string) {
  if (kind === "points") {
    useZoneCreateStore.getState().end();
    usePointEditStore.getState().start(fieldId);
  } else {
    usePointEditStore.getState().end();
    useZoneCreateStore.getState().start(fieldId);
  }
  useUiStore.getState().openMap(fieldId);
}

export function SoilSampleActions(props: {
  kind: "zones" | "points";
  fieldId: string;
  busy: boolean;
  onUploadOrder: (file: File) => void;
  onUploadWithResults: (file: File) => void;
}) {
  const { kind, fieldId, busy, onUploadOrder, onUploadWithResults } = props;
  const orderFileRef = useRef<HTMLInputElement>(null);
  const sourceFileRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        className={SOIL_SAMPLE_BTN}
        onClick={() => startCreate(kind, fieldId)}
      >
        Create {kind} &amp; order sampling
      </button>
      <button
        disabled={busy}
        className={SOIL_SAMPLE_BTN}
        onClick={() => orderFileRef.current?.click()}
      >
        Upload {kind} &amp; order sampling
      </button>
      <input
        ref={orderFileRef}
        type="file"
        aria-label={`Upload ${kind} to order file`}
        accept={ORDER_ACCEPT[kind]}
        className="hidden"
        onChange={(e) =>
          takeFile(orderFileRef.current, onUploadOrder, e.target.files?.[0])
        }
      />
      <button
        disabled={busy}
        className={SOIL_SAMPLE_BTN}
        onClick={() => sourceFileRef.current?.click()}
      >
        {busy ? <Loader2 size={11} className="inline animate-spin" /> : null}{" "}
        Upload {kind} with results…
      </button>
      <span className="text-[10px] text-faint">{FORMAT_HINT[kind]}</span>
      <input
        ref={sourceFileRef}
        type="file"
        aria-label={`Upload ${kind} with results file`}
        accept={ORDER_ACCEPT[kind]}
        className="hidden"
        onChange={(e) =>
          takeFile(
            sourceFileRef.current,
            onUploadWithResults,
            e.target.files?.[0],
          )
        }
      />
    </div>
  );
}
